"use client";

export interface DOMError extends Error {
  type?: string;
  target?: Element;
  details?: any;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: DOMError[] = [];

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  init() {
    if (typeof window === 'undefined') return;    // Отлавливаем DOM ошибки
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function<T extends Node>(child: T): T {
      try {
        if (!this.contains(child)) {
          const warningDetails = {
            operation: 'removeChild',
            parentNodeName: this.nodeName,
            parentClassName: (this as any).className || 'no-class',
            childNodeName: child.nodeName,
            childClassName: (child as any).className || 'no-class',
            parentId: (this as any).id || 'no-id',
            childId: (child as any).id || 'no-id'
          };
          
          console.warn('[DOM Safe] Attempted to remove child that is not a child of this node', warningDetails);
          
          // Логируем как предупреждение, а не как ошибку
          ErrorHandler.getInstance().logError({
            name: 'DOM Warning',
            message: 'Attempted to remove child that is not a child of this node'
          } as DOMError, warningDetails);
          
          return child;
        }
        return originalRemoveChild.call(this, child) as T;
      } catch (error) {
        const errorDetails = {
          operation: 'removeChild',
          parentNodeName: this.nodeName,
          parentClassName: (this as any).className || 'no-class',
          childNodeName: child.nodeName,
          childClassName: (child as any).className || 'no-class',
          parentId: (this as any).id || 'no-id',
          childId: (child as any).id || 'no-id',
          originalError: error
        };
        
        console.error('[DOM Error] removeChild failed:', error);
        ErrorHandler.getInstance().logError(error as DOMError, errorDetails);
        return child;
      }
    };    // Отлавливаем appendChild ошибки
    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function<T extends Node>(child: T): T {
      try {
        return originalAppendChild.call(this, child) as T;
      } catch (error) {
        const errorDetails = {
          operation: 'appendChild',
          parentNodeName: this.nodeName,
          parentClassName: (this as any).className || 'no-class',
          childNodeName: child.nodeName,
          childClassName: (child as any).className || 'no-class',
          parentId: (this as any).id || 'no-id',
          childId: (child as any).id || 'no-id',
          originalError: error
        };
        
        console.error('[DOM Error] appendChild failed:', error);
        ErrorHandler.getInstance().logError(error as DOMError, errorDetails);
        throw error;
      }
    };

    // Глобальный обработчик ошибок
    window.addEventListener('error', (event) => {
      if (event.error?.message?.includes('removeChild') || 
          event.error?.message?.includes('appendChild')) {
        this.logError(event.error, {
          type: 'global',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        });
      }
    });
  }
  logError(error: DOMError, details?: any) {
    const errorEntry: DOMError = {
      ...error,
      details: {
        ...details,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        url: window.location.href
      }
    };
    
    this.errorLog.push(errorEntry);
    
    // Отправляем в консоль для отладки
    console.error('[ErrorHandler] DOM Error logged:', errorEntry);
    
    // Отправляем ошибку в Telegram
    this.sendErrorToTelegram(errorEntry);
  }

  private sendErrorToTelegram(errorEntry: DOMError) {
    try {
      const { message, name, stack, details } = errorEntry;
      const place = `Place: DOM Error Handler`;
      const addInfo = {
        userAgent: details?.userAgent || 'unknown',
        platform: details?.platform || 'unknown',
        language: details?.language || 'unknown',
        currentUrl: details?.url || 'unknown',
        operation: details?.operation || 'unknown',
        timestamp: details?.timestamp || new Date().toISOString()
      };
      
      const sendmsg = `🔴 DOM ERROR\n\n<b>${message || 'Unknown DOM Error'}</b>\n\n<b>Name:</b> ${name || 'DOMError'}\n\n<b>Stack:</b>\n${stack || 'No stack trace'}\n\n<b>Details:</b>\n${JSON.stringify(addInfo, null, 2)}\n\n<b>⚠️${place}⚠️</b>`;
      
      const encodedMsg = encodeURIComponent(sendmsg);
      
      fetch(`https://api.telegram.org/bot7506786216:AAE30KEWCELe7rdm48dPOkUyXbd182-_Jvw/sendMessage?chat_id=-4652360322&parse_mode=html&text=${encodedMsg}`)
        .then(res => console.log('[ErrorHandler] Error sent to Telegram:', res.status))
        .catch(error => console.error('[ErrorHandler] Failed to send error to Telegram:', error));
    } catch (telegramError) {
      console.error('[ErrorHandler] Error while sending to Telegram:', telegramError);
    }
  }

  getErrorLog(): DOMError[] {
    return [...this.errorLog];
  }

  clearErrorLog() {
    this.errorLog = [];
  }
}

export default ErrorHandler;
